import { FeatureFlagGuard } from "@/components/access-control/FeatureFlagGuard";
import {
  PermissionGuard,
  RoleGuard,
} from "@/components/access-control/PermissionGuard";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { LoginCallback } from "@/components/auth/LoginCallback";
import { LogoutIdleTimer } from "@/components/auth/LogoutIdleTimer";
import { TopBanner } from "@/components/layout/TopBanner";
import { Loading } from "@/components/Loading";
import { LoadingScreen } from "@/components/LoadingScreen";
import { useBackendHealthService } from "@/hooks/useBackendHealthService";
import useFeatureFlag from "@/hooks/useFeatureFlag";
import { MaintenancePage } from "@/pages/MaintenancePage";
import type { ElementType } from "react";
import { lazy, Suspense } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from "react-router-dom";

function Loadable(Component: ElementType) {
  return function LoadableComponent<Props extends object>(props: Props) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <Component {...props} />
      </Suspense>
    );
  };
}

const DashboardPage = Loadable(
  lazy(() =>
    import("@/pages/DashboardPage").then((module) => ({
      default: module.DashboardPage,
    })),
  ),
);
const AnalyticsPage = Loadable(
  lazy(() =>
    import("@/pages/AnalyticsPage").then((module) => ({
      default: module.AnalyticsPage,
    })),
  ),
);
const CampaignsPage = Loadable(
  lazy(() =>
    import("@/pages/campaigns/CampaignsPage").then((module) => ({
      default: module.CampaignsPage,
    })),
  ),
);
const CreateOrEditCampaignPage = Loadable(
  lazy(() =>
    import("@/pages/campaigns/CreateOrEditCampaignPage").then((module) => ({
      default: module.CreateOrEditCampaignPage,
    })),
  ),
);
const CampaignDetailsPage = Loadable(
  lazy(() =>
    import("@/pages/campaigns/CampaignDetailsPage").then((module) => ({
      default: module.CampaignDetailsPage,
    })),
  ),
);
const CampaignApplicationsPage = Loadable(
  lazy(() =>
    import("@/pages/campaigns/CampaignApplicationsPage").then((module) => ({
      default: module.CampaignApplicationsPage,
    })),
  ),
);
const CampaignOrdersPage = Loadable(
  lazy(() =>
    import("@/pages/campaigns/orders/CampaignOrdersPage").then((module) => ({
      default: module.CampaignOrdersPage,
    })),
  ),
);
const CampaignOrderDetailsPage = Loadable(
  lazy(() =>
    import("@/pages/campaigns/orders/CampaignOrderDetailsPage").then(
      (module) => ({
        default: module.CampaignOrderDetailsPage,
      }),
    ),
  ),
);
const CreateOrderPage = Loadable(
  lazy(() =>
    import("@/pages/campaigns/orders/CreateOrderPage").then((module) => ({
      default: module.CreateOrderPage,
    })),
  ),
);
const CustomersPage = Loadable(
  lazy(() =>
    import("@/pages/customers/CustomersPage").then((module) => ({
      default: module.CustomersPage,
    })),
  ),
);
const CustomerDetailsPage = Loadable(
  lazy(() =>
    import("@/pages/customers/CustomerDetailsPage").then((module) => ({
      default: module.CustomerDetailsPage,
    })),
  ),
);
const ProfilePage = Loadable(
  lazy(() =>
    import("@/pages/ProfilePage").then((module) => ({
      default: module.ProfilePage,
    })),
  ),
);
const AdminSettingsPage = Loadable(
  lazy(() =>
    import("@/pages/admin/AdminSettingsPage").then((module) => ({
      default: module.AdminSettingsPage,
    })),
  ),
);

const QuotationPage = Loadable(
  lazy(() =>
    import("@/pages/QuotationPage").then((module) => ({
      default: module.QuotationPage,
    })),
  ),
);

function Router() {
  const { enabled: isMaintenancePageEnabled } = useFeatureFlag("maintenance");
  const { isBackendHealthy } = useBackendHealthService(
    isMaintenancePageEnabled,
  );

  const maintenanceRoutes = createBrowserRouter([
    {
      path: "*",
      element: <Navigate replace to="/maintenance" />,
    },
    {
      path: "/maintenance",
      element: <MaintenancePage />,
    },
  ]);

  const mainRoutes = createBrowserRouter([
    {
      path: "/",
      element: (
        <AuthGuard>
          <LogoutIdleTimer>
            <>
              <TopBanner />
              <Outlet />
            </>
          </LogoutIdleTimer>
        </AuthGuard>
      ),
      children: [
        {
          index: true,
          element: <Navigate replace to="/dashboard" />,
        },
        { path: "dashboard", element: <DashboardPage /> },
        {
          path: "analytics",
          element: (
            <RoleGuard allowedRoles={["everbay_admin"]}>
              <AnalyticsPage />
            </RoleGuard>
          ),
        },
        {
          path: "campaigns",
          children: [
            { index: true, element: <CampaignsPage /> },
            {
              path: ":id",
              element: <CampaignDetailsPage />,
            },
            {
              path: ":id/edit",
              element: <CreateOrEditCampaignPage isEdit />,
            },
            {
              path: "create",
              element: <CreateOrEditCampaignPage />,
            },
            {
              path: "applications",
              element: (
                <PermissionGuard requiredPermission="read:contact_board">
                  <CampaignApplicationsPage />
                </PermissionGuard>
              ),
            },
            {
              path: "orders",
              element: (
                <PermissionGuard requiredPermission="write:campaign">
                  <CampaignOrdersPage />
                </PermissionGuard>
              ),
            },
            {
              path: "orders/create",
              element: (
                <RoleGuard allowedRoles={["everbay_admin", "employee"]}>
                  <CreateOrderPage />
                </RoleGuard>
              ),
            },
            {
              path: "orders/:id",
              element: <CampaignOrderDetailsPage />,
            },
          ],
        },
        {
          path: "customers",
          element: <PermissionGuard requiredPermission="write:user" />,
          children: [
            { index: true, element: <CustomersPage /> },
            {
              path: ":id",
              element: <CustomerDetailsPage />,
            },
          ],
        },
        { path: "profile", element: <ProfilePage /> },
        {
          path: "quotation-form",
          element: (
            <FeatureFlagGuard flag="quotations">
              <PermissionGuard requiredPermission="create:quotation">
                <QuotationPage />
              </PermissionGuard>
            </FeatureFlagGuard>
          ),
        },
        {
          path: "admin/settings",
          element: (
            <RoleGuard allowedRoles={["everbay_admin", "employee"]}>
              <AdminSettingsPage />
            </RoleGuard>
          ),
        },
        { path: "*", element: <Navigate replace to="/dashboard" /> },
      ],
    },
    { path: "/callback", element: <LoginCallback /> },
    { path: "/maintenance", element: <Navigate replace to="/dashboard" /> },
  ]);

  if (isMaintenancePageEnabled) {
    if (isBackendHealthy == null) return <Loading />;
    if (!isBackendHealthy) return <RouterProvider router={maintenanceRoutes} />;
  }

  return <RouterProvider router={mainRoutes} />;
}

export { Router };
